import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { motion, MotionStyle, useReducedMotion } from 'motion/react';
import {
  IconDefinition,
  faAddressCard,
  faPalette,
  faQrcode,
  faRocket,
  faCrown,
  faUsers,
  faCopy,
  faWindow,
  faAddressBook,
  faFileCsv,
  faUser,
  faUserGroup,
  faArrowLeft,
  faArrowRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ConsentDialogContent } from '@components/ConsentDialog/ConsentDialogContent';
import { PaymentSection } from '@components/UpgradeDialog/PaymentSection';
import { analytics } from '@modules/analytics';
import { Modal, Dialog, Button, Heading, Text, Box, DialogCloseButton, Stack } from '@ui-lib';

import heroImage from './panel-hero-image.png';

import * as styles from './UpgradeAndConsentDialog.css';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { BlinqLogo } from '@components/BlinqAssets';
import { useFeatureFlag } from '@modules/feature-flag';

type UpgradeAndConsentDialogProps = {
  /**
   * Flag to indicate if the dialog should be shown
   */
  show: boolean;
  /** Callback function that is called when the dialog is closed */
  onClose?: () => void;
  /** Type of dialog to show, defaults to 'upgrade' */
  type?: 'consent' | 'upgrade' | 'both';
  /** Source of the modal open event */
  trackingSource?: string;
};

export const UpgradeAndConsentDialog: React.FC<UpgradeAndConsentDialogProps> = ({
  show,
  onClose,
  type = 'upgrade',
  trackingSource,
}) => {
  const [variant, { expose, loading: loadingFlag }] = useFeatureFlag('targeted-paywall', 'control');

  const { loading: billingSubscriptionLoading, planType } = useActorPlan();
  const [showModal, setShowModal] = useState(false);
  const [subDialog, setSubDialog] = useState<1 | 2>(type === 'consent' ? 2 : 1);

  useEffect(() => {
    setSubDialog(type === 'consent' ? 2 : 1);
  }, [type]);

  // Temp: delay open until plan type so correct content is showing
  useEffect(() => {
    if (show && !billingSubscriptionLoading && !loadingFlag) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [show, billingSubscriptionLoading, loadingFlag]);

  // Targeted paywall experiment
  const showUserIntention = variant === 'treatment' && planType === 'free';
  useEffect(() => {
    if (show && planType === 'free') {
      // Expose experiment when user is on free plan only
      expose();
    }
  }, [show, planType, expose]);

  useEffect(() => {
    if (show) {
      // Logs modal view
      analytics.page({
        name: 'modal_view',
        properties: {
          ...(type === 'consent' && { title: 'Consent dialog' }),
          ...(type === 'upgrade' && { title: 'Upgrade plan dialog' }),
          ...(type === 'both' && { title: 'Upgrade and Consent dialog flow' }),
          open_source: trackingSource,
        },
      });
    }
  }, [show, type, trackingSource]);

  return (
    <Modal open={showModal} handleClose={() => onClose && type === 'upgrade' && onClose()}>
      <div className={styles.root}>
        <Dialog>
          {type === 'both' && (
            <MotionDiv slideCount={2} currentSlide={subDialog}>
              <div aria-hidden={subDialog !== 1}>
                <UpgradePane
                  showUserIntention={showUserIntention}
                  planType={planType}
                  onClose={onClose}
                  onNext={() => setSubDialog(2)}
                />
              </div>
              <div aria-hidden={subDialog !== 2}>
                <ConsentDialogContent
                  onBack={() => {
                    setSubDialog(1);
                  }}
                  onCloseDialog={onClose}
                />
              </div>
            </MotionDiv>
          )}
          {type === 'consent' && <ConsentDialogContent onCloseDialog={onClose} />}
          {type === 'upgrade' && (
            <UpgradePane showUserIntention={showUserIntention} planType={planType} onClose={onClose} />
          )}
        </Dialog>
      </div>
    </Modal>
  );
};

// Upgrade Sub Dialog Inner
type UpgradePane = {
  onNext?: () => void;
  onClose?: () => void;
  planType: 'free' | 'premium' | 'team';
  showUserIntention: boolean;
};

const UpgradePane: React.FC<UpgradePane> = ({ onNext, onClose, planType, showUserIntention }) => {
  const [step, setStep] = useState<1 | 2 | 3>(showUserIntention ? 1 : 2);
  // prevent height changes when switching between steps
  const panesRef = useRef<HTMLDivElement>(null);
  const [paneHeight, setPaneHeight] = useState<number | '100%'>('100%');

  const [userIntention, setUserIntention] = useState<'individual' | 'team' | 'unsure'>('unsure');
  const [selectedPlan, setSelectedPlan] = useState<'premium' | 'business'>('premium');

  useEffect(() => {
    if (panesRef.current) {
      const measuredHeight = panesRef.current.offsetHeight;
      setPaneHeight(measuredHeight);
    }
  }, []);

  const showUpgradeOption = useMemo(() => {
    if (planType === 'premium') {
      return 'business';
    }

    if (planType === 'free') {
      switch (true) {
        case userIntention === 'individual':
          return 'premium';
        case userIntention === 'team':
          return 'business';
        case userIntention === 'unsure':
          return 'both';
        default:
          return 'both';
      }
    }

    // They will only see this if they have cancelled, so try resubscribe to business

    return 'both';
  }, [planType, userIntention]);

  const handleUserIntentionSelection = (intent: 'individual' | 'team' | 'unsure') => {
    setUserIntention(intent);
    setStep(2);
  };

  const handlePlanSelection = (plan: 'premium' | 'business') => {
    setSelectedPlan(plan);
    setStep(3);
  };

  return (
    <div className={styles.inner}>
      <MotionDiv slideCount={3} currentSlide={step}>
        <div aria-hidden={step !== 1}>
          {showUserIntention && <UserIntentPanel onUserIntentSelected={handleUserIntentionSelection} />}
        </div>
        <div
          className={styles.panesWrapper}
          ref={panesRef}
          aria-hidden={step !== 2}
          style={{ height: `${paneHeight}px` }}
        >
          {showUserIntention && (
            <div className={styles.backButtonWrapper}>
              <Button
                onClick={() => setStep(1)}
                variant="text"
                iconLeading={<FontAwesomeIcon icon={faArrowLeft} />}
              />
            </div>
          )}
          <div className={styles.panes}>
            {(showUpgradeOption === 'both' || showUpgradeOption === 'premium') && (
              <UpgradeOption
                title="Blinq Premium"
                subtitle="For individuals"
                lead="More customizability. Create professional cards that look and feel your own"
                buttonText="Start 7-day free trial"
                buttonLead="No commitment, cancel anytime."
                onClick={() => handlePlanSelection('premium')}
                benefits={premiumBenefits}
              />
            )}
            {(showUpgradeOption === 'both' || showUpgradeOption === 'business') && (
              <UpgradeOption
                title="Blinq Business"
                subtitle="For teams"
                lead="All the tools to enable consistent branding across your entire team"
                buttonText="Start 30-day free trial"
                buttonLead="No commitment, cancel anytime."
                onClick={() => handlePlanSelection('business')}
                benefits={businessBenefits}
              />
            )}
          </div>
        </div>
        <div aria-hidden={step !== 3}>
          <PaymentSection
            onBack={() => setStep(2)}
            type={selectedPlan === 'premium' ? 'INDIVIDUAL' : 'TEAM'}
            includeBackBtn
          />
        </div>
      </MotionDiv>

      <div className={styles.panelHero}>
        <div className={styles.closeButton}>
          {onNext ? (
            <Button
              iconTrailing={<FontAwesomeIcon icon={faArrowRight} />}
              variant="outline"
              onClick={() => onNext()}
            >
              Maybe Later
            </Button>
          ) : (
            <DialogCloseButton onClose={() => onClose?.()} />
          )}
        </div>
        <img className={styles.panelHeroImage} src={heroImage.src} alt="example blinq app screen" />
      </div>
    </div>
  );
};

type UserIntentPanel = {
  onUserIntentSelected: (intent: 'individual' | 'team' | 'unsure') => void;
};

export const UserIntentPanel: React.FC<UserIntentPanel> = ({ onUserIntentSelected }) => {
  return (
    <div className={styles.intentionPane}>
      <Box marginBottom={'xlarge'}>
        <BlinqLogo className={styles.logo} />
      </Box>
      <Box gap="medium" marginBottom={'xlarge'} display={'flex'} flexDirection={'column'}>
        <Heading level="4">Which describes you best?</Heading>
        <Text size="medium">This helps us tailor the best experience for you.</Text>
      </Box>
      <Stack gap="medium">
        <Button
          variant="outline"
          iconLeading={<FontAwesomeIcon icon={faUser} />}
          onClick={() => onUserIntentSelected('individual')}
        >
          Using Blinq for myself
        </Button>
        <Button
          variant="outline"
          iconLeading={<FontAwesomeIcon icon={faUserGroup} />}
          onClick={() => onUserIntentSelected('team')}
        >
          Using Blinq for my team
        </Button>
        <Button
          iconTrailing={<FontAwesomeIcon icon={faArrowRight} />}
          variant="text"
          onClick={() => onUserIntentSelected('unsure')}
        >
          I&apos;m not sure yet
        </Button>
      </Stack>
    </div>
  );
};

// Upgrade Plan Option
type UpgradeOptionProps = {
  benefits: { icon: IconDefinition; text: string }[];
  title: string;
  subtitle: string;
  lead: string;
  buttonText: string;
  buttonLead: string;
  onClick: () => void;
};

const UpgradeOption: React.FC<UpgradeOptionProps> = ({
  benefits,
  title,
  buttonText,
  subtitle,
  buttonLead,
  lead,
  onClick,
}) => {
  return (
    <Box style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
      <Box marginBottom={'large'}>
        <Heading level="4">{title}</Heading>
      </Box>
      <Box marginBottom={'medium'}>
        <Text weight="demibold" size="large" tone="brandAccent">
          {subtitle}
        </Text>
      </Box>
      <Box marginBottom={'medium'}>
        <Text size="small" className={styles.lead}>
          {lead}
        </Text>
      </Box>
      <Box as="ol" marginBottom={'xlarge'}>
        {benefits.map((b) => (
          <div key={b.text} className={styles.benefit}>
            <FontAwesomeIcon icon={b.icon} />
            <Text size="small" className={styles.benefitText} weight="demibold">
              {b.text}
            </Text>
          </div>
        ))}
      </Box>
      <Box marginTop={'auto'} marginBottom={'small'}>
        <Button onClick={onClick} width="full">
          {buttonText}
        </Button>
      </Box>
      <Text align={'center'} size="small">
        {buttonLead}
      </Text>
    </Box>
  );
};

// Animator
type MotionDivProps = {
  currentSlide: 1 | 2 | 3;
  slideCount: number;
  children: ReactNode;
};

const MotionDiv: React.FC<MotionDivProps> = ({ slideCount, currentSlide, children }) => {
  const shouldReduceMotion = useReducedMotion();

  // Function to determine the x position based on the currentSlide
  const XPosition = useMemo(() => {
    const slideWidthPercent = 100 / slideCount;
    const position = -(slideWidthPercent * (currentSlide - 1));
    return `${position}%`;
  }, [currentSlide, slideCount]);

  return (
    <div className={styles.motionOuter}>
      <motion.div
        initial={{ x: XPosition }}
        animate={{ x: XPosition }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.3,
          ease: 'easeInOut',
        }}
        style={
          {
            gridTemplateColumns: `repeat(${slideCount}, 1fr)`,
            width: `${slideCount * 100}%`,
          } as MotionStyle
        }
        className={styles.motionInner}
      >
        {children}
      </motion.div>
    </div>
  );
};

// Benefit Name / Icon map
const premiumBenefits = [
  {
    icon: faAddressCard,
    text: 'Create up to 5 cards for yourself. A card for every occasion.',
  },
  {
    icon: faPalette,
    text: 'Add a custom color to your cards.',
  },
  {
    icon: faQrcode,
    text: 'Customizable QR codes.',
  },
  {
    icon: faRocket,
    text: 'Remove Blinq branding from a card when shared.',
  },
  {
    icon: faFileCsv,
    text: 'Export your contacts.',
  },
];

const businessBenefits = [
  {
    icon: faCrown,
    text: 'All team members receive Blinq Premium accounts.',
  },
  {
    icon: faUsers,
    text: 'Create cards for your entire team.',
  },
  {
    icon: faCopy,
    text: 'Create templates to enforce consistency across cards.',
  },
  {
    icon: faAddressBook,
    text: 'Shared corporate contact book.',
  },
  {
    icon: faWindow,
    text: 'Administrative card control for peace of mind.',
  },
];
